import { useEffect, useState } from "react";

/** How often a relative time ("Finished 2 minutes ago") is worked out again. */
export const NOW_TICK_MS = 30_000;

function visible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

/**
 * The current time, renewed every 30 seconds while the component is mounted and the tab is
 * visible (#1260). A screen that words a past instant relative to now reads this, so the words
 * move on their own while the page stays open. A hidden tab does not tick, and catches up the
 * moment it is shown again.
 */
export function useNow(intervalMs: number = NOW_TICK_MS): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const start = () => {
      if (timer === null) timer = setInterval(() => setNow(new Date()), intervalMs);
    };
    const onVisibility = () => {
      if (visible()) {
        setNow(new Date());
        start();
      } else {
        stop();
      }
    };
    if (visible()) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
  return now;
}
